import AudioRecordingWorklet from "./audio-processing.ts";
import { createWorketFromSrc } from "./audioworklet-registry.ts";
import EventEmitter from "eventemitter3";

export type GetAudioContextOptions = AudioContextOptions & {
  id?: string;
};

const map: Map<string, AudioContext> = new Map();

// Helper to detect browser type
const getBrowser = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.indexOf("firefox") > -1) return "firefox";
  if (userAgent.indexOf("safari") > -1 && userAgent.indexOf("chrome") === -1)
    return "safari";
  return "chrome";
};

export const audioContext: (
  options?: GetAudioContextOptions,
) => Promise<AudioContext> = (() => {
  const didInteract = new Promise((res) => {
    window.addEventListener("pointerdown", res, { once: true });
    window.addEventListener("keydown", res, { once: true });
  });

  return async (options?: GetAudioContextOptions) => {
    try {
      const browser = getBrowser();
      const a = new Audio();
      a.src =
        "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";
      await a.play();

      if (options?.id && map.has(options.id)) {
        const ctx = map.get(options.id);
        if (ctx) {
          return ctx;
        }
      }

      // For Firefox, we need to match the input stream's sample rate
      if (browser === "firefox") {
        // We'll set the actual sample rate after getting the stream
        const ctx = new AudioContext();
        if (options?.id) {
          map.set(options.id, ctx);
        }
        return ctx;
      }

      const ctx = new AudioContext(options);
      if (options?.id) {
        map.set(options.id, ctx);
      }
      return ctx;
    } catch (e) {
      await didInteract;
      if (options?.id && map.has(options.id)) {
        const ctx = map.get(options.id);
        if (ctx) {
          return ctx;
        }
      }
      const ctx = new AudioContext(options);
      if (options?.id) {
        map.set(options.id, ctx);
      }
      return ctx;
    }
  };
})();

export class AudioRecorder extends EventEmitter {
  stream: MediaStream | undefined | null;
  audioContext: AudioContext | undefined | null;
  source: MediaStreamAudioSourceNode | undefined | null;
  recording: boolean = false;
  recordingWorklet: AudioWorkletNode | undefined | null;
  browser: string;
  private scriptProcessor: ScriptProcessorNode | undefined | null;

  constructor(public sampleRate = 16000) {
    super();
    this.browser = this.getBrowser();
  }

  private getBrowser() {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.indexOf("firefox") > -1) return "firefox";
    if (userAgent.indexOf("safari") > -1 && userAgent.indexOf("chrome") === -1)
      return "safari";
    return "chrome";
  }

  // Simple linear resampling
  private resampleBuffer(
    input: Float32Array,
    inputSampleRate: number,
    outputSampleRate: number,
  ): Float32Array {
    const ratio = inputSampleRate / outputSampleRate;
    const newLength = Math.round(input.length / ratio);
    const output = new Float32Array(newLength);

    for (let i = 0; i < newLength; i++) {
      const position = i * ratio;
      const index = Math.floor(position);
      const fraction = position - index;

      if (index + 1 < input.length) {
        output[i] = input[index] * (1 - fraction) + input[index + 1] * fraction;
      } else {
        output[i] = input[index];
      }
    }

    return output;
  }

  async start() {
    try {
      if (this.browser === "firefox" || this.browser === "safari") {
        // Get stream with minimal constraints
        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });

        // Create audio context
        this.audioContext = new AudioContext();

        // Create source
        this.source = this.audioContext.createMediaStreamSource(this.stream);

        const bufferSize = 2048;
        this.scriptProcessor = this.audioContext.createScriptProcessor(
          bufferSize,
          1,
          1,
        );

        // Keep track of accumulated samples for resampling
        let accumulatedSamples = new Float32Array(0);
        const outputSamplesNeeded = Math.ceil(
          bufferSize * (this.sampleRate / this.audioContext.sampleRate),
        );

        this.scriptProcessor.onaudioprocess = (e) => {
          const inputData = e.inputBuffer.getChannelData(0);

          // Accumulate samples
          const newAccumulated = new Float32Array(
            accumulatedSamples.length + inputData.length,
          );
          newAccumulated.set(accumulatedSamples);
          newAccumulated.set(inputData, accumulatedSamples.length);
          accumulatedSamples = newAccumulated;

          // Once we have enough samples, resample and emit
          if (accumulatedSamples.length >= outputSamplesNeeded) {
            // Resample to target rate
            const resampled = this.resampleBuffer(
              accumulatedSamples,
              this.audioContext!.sampleRate,
              this.sampleRate,
            );

            // Convert to 16-bit PCM
            const output = new Int16Array(resampled.length);
            for (let i = 0; i < resampled.length; i++) {
              // Apply amplitude correction and convert to 16-bit
              const sample = Math.max(-1, Math.min(1, resampled[i] * 3.0)); // Boost amplitude
              output[i] = Math.round(sample * 32767);
            }

            const arrayBufferString = this.arrayBufferToBase64(output.buffer);
            this.emit("data", arrayBufferString);

            // Keep remaining samples
            accumulatedSamples = new Float32Array(0);
          }
        };

        // Connect nodes
        this.source.connect(this.scriptProcessor);
        this.scriptProcessor.connect(this.audioContext.destination);
      } else {
        // Chrome implementation unchanged
        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            sampleRate: this.sampleRate,
          },
        });

        this.audioContext = new AudioContext({ sampleRate: this.sampleRate });
        this.source = this.audioContext.createMediaStreamSource(this.stream);

        const workletName = "audio-recorder-worklet";
        const blob = new Blob([AudioRecordingWorklet], {
          type: "application/javascript",
        });
        const workletUrl = URL.createObjectURL(blob);

        await this.audioContext.audioWorklet.addModule(workletUrl);
        URL.revokeObjectURL(workletUrl);

        this.recordingWorklet = new AudioWorkletNode(
          this.audioContext,
          workletName,
        );

        this.recordingWorklet.port.onmessage = (ev: MessageEvent) => {
          const arrayBuffer = ev.data.data.int16arrayBuffer;
          if (arrayBuffer) {
            const base64 = this.arrayBufferToBase64(arrayBuffer);
            this.emit("data", base64);
          }
        };

        this.source.connect(this.recordingWorklet);
      }

      this.recording = true;
    } catch (err) {
      console.error("Error starting audio recorder:", err);
      this.recording = false;
      throw err;
    }
  }

  async stop() {
    try {
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }

      if (this.source) {
        this.source.disconnect();
        this.source = null;
      }

      if (this.recordingWorklet) {
        this.recordingWorklet.disconnect();
        this.recordingWorklet = null;
      }

      if (this.scriptProcessor) {
        this.scriptProcessor.disconnect();
        this.scriptProcessor = null;
      }

      if (this.audioContext && this.audioContext.state !== "closed") {
        await this.audioContext.close();
        this.audioContext = null;
      }

      this.recording = false;
    } catch (error) {
      console.error("Error stopping audio recorder:", error);
    }
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
}
